import React, { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, UserPlus, X, Save, Coins } from "lucide-react";
import { adminApi, formatErr } from "@/admin/api";
import { Panel, AdminInput, AdminSelect, EmptyState, StatusPill } from "@/admin/components/ui";
import ManageCreditsModal from "@/admin/components/ManageCreditsModal";

const empty = { name: "", surname: "", email: "", status: "active" };

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [creditsCustomer, setCreditsCustomer] = useState(null);

  const load = () => {
    setLoading(true);
    adminApi.get("/admin/customers").then((r) => setCustomers(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const list = customers.filter((c) =>
    (filter === "all" || c.status === filter) &&
    (!q || `${c.name} ${c.surname} ${c.email}`.toLowerCase().includes(q.toLowerCase()))
  );

  const save = async () => {
    if (!form.name || !form.surname || !form.email) {
      toast.error("Name, surname and email are required."); return;
    }
    try {
      if (editing === "new") {
        await adminApi.post("/admin/customers", form);
        toast.success("Customer added.");
      } else {
        await adminApi.put(`/admin/customers/${editing}`, { name: form.name, surname: form.surname, status: form.status });
        toast.success("Customer updated.");
      }
      setEditing(null); setForm(empty); load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name} ${c.surname}?`)) return;
    try {
      await adminApi.delete(`/admin/customers/${c.id}`);
      toast.success("Customer removed.");
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div data-testid="page-admin-customers" className="space-y-6">
      <SEO title="AdminCustomers · WelkomBiz Photography" noindex />
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 glass chrome-border rounded-full px-4 py-2">
          <Search className="w-4 h-4 text-white/45" />
          <input data-testid="customers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…" className="flex-1 bg-transparent text-sm text-white placeholder-white/35 outline-none font-body" />
        </div>
        <button data-testid="customers-add" onClick={() => { setForm(empty); setEditing("new"); }} className="btn-primary !py-2.5 !px-5 !text-[11px]">
          <UserPlus className="w-3.5 h-3.5" /> Add Customer
        </button>
      </div>

      <div className="flex gap-2">
        {["all", "active", "inactive"].map((s) => (
          <button key={s} data-testid={`customers-filter-${s}`} onClick={() => setFilter(s)} className={`text-[10px] tracking-luxe uppercase px-3 py-1.5 rounded-full border ${filter === s ? "border-transparent bg-gradient-to-r from-[#ff2e93] to-[#18d3ff] text-white" : "border-white/15 text-white/55 hover:text-white"}`}>{s}</button>
        ))}
      </div>

      {editing && (
        <Panel testId="customer-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AdminInput data-testid="customer-input-name" label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <AdminInput data-testid="customer-input-surname" label="Surname" value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} />
            <AdminInput data-testid="customer-input-email" label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={editing !== "new"} />
            <AdminSelect data-testid="customer-input-status" label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="active" className="bg-black">Active</option>
              <option value="inactive" className="bg-black">Inactive</option>
            </AdminSelect>
          </div>
          <div className="flex gap-2 mt-5">
            <button data-testid="customer-save" onClick={save} className="btn-primary !py-2.5 !px-5 !text-[11px]"><Save className="w-3.5 h-3.5" /> Save</button>
            <button onClick={() => { setEditing(null); setForm(empty); }} className="btn-ghost !py-2.5 !px-5 !text-[11px]"><X className="w-3.5 h-3.5" /> Cancel</button>
          </div>
        </Panel>
      )}

      {loading ? <p className="text-sm text-white/40">Loading…</p> :
       list.length === 0 ? <EmptyState testId="customers-empty" title="No customers yet" message="Customers can be added manually here, or will register themselves once Phase 3 unlocks." /> :
       (
        <Panel className="!p-0 overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/3 text-[10px] tracking-luxe uppercase text-white/45">
                <tr>
                  <th className="text-left px-5 py-3">Name</th>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-left px-5 py-3">Credits</th>
                  <th className="text-left px-5 py-3">Purchases</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id} className="border-t border-white/5 hover:bg-white/3" data-testid={`customer-row-${c.id}`}>
                    <td className="px-5 py-4 text-white">{c.name} {c.surname}</td>
                    <td className="px-5 py-4 text-white/70">{c.email}</td>
                    <td className="px-5 py-4 text-white/70">{c.credit_balance ?? 0}</td>
                    <td className="px-5 py-4 text-white/70">{c.purchases ?? 0}</td>
                    <td className="px-5 py-4"><StatusPill status={c.status} /></td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setCreditsCustomer(c)} data-testid={`customer-credits-${c.id}`} className="p-2 rounded-md border border-white/10 text-white/70 hover:text-[#7fe6ff]" title="Manage credits"><Coins className="w-4 h-4" /></button>
                        <button onClick={() => { setForm({ ...empty, ...c }); setEditing(c.id); }} data-testid={`customer-edit-${c.id}`} className="p-2 rounded-md border border-white/10 text-white/70 hover:text-white"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => remove(c)} data-testid={`customer-delete-${c.id}`} className="p-2 rounded-md border border-white/10 text-[#ff7fc1]"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden divide-y divide-white/5">
            {list.map((c) => (
              <div key={c.id} className="p-4" data-testid={`customer-card-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white">{c.name} {c.surname}</p>
                    <p className="text-xs text-white/55 truncate">{c.email}</p>
                  </div>
                  <StatusPill status={c.status} />
                </div>
                <p className="text-[10px] tracking-luxe uppercase text-white/45 mt-2">{c.credit_balance ?? 0} credits · {c.purchases ?? 0} purchases</p>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => setCreditsCustomer(c)} data-testid={`customer-credits-card-${c.id}`} className="px-3 py-2 rounded-md border border-white/10 text-[#7fe6ff]"><Coins className="w-4 h-4" /></button>
                  <button onClick={() => { setForm({ ...empty, ...c }); setEditing(c.id); }} className="flex-1 text-[10px] tracking-luxe uppercase py-2 rounded-md border border-white/10 text-white/70">Edit</button>
                  <button onClick={() => remove(c)} className="px-3 py-2 rounded-md border border-white/10 text-[#ff7fc1]"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </Panel>
       )}
       {creditsCustomer && (
         <ManageCreditsModal
           customer={creditsCustomer}
           onClose={() => setCreditsCustomer(null)}
           onUpdated={load}
         />
       )}
    </div>
  );
}
